import { useState, useEffect, createContext, useContext } from 'react';
import superAdminAuthService from '../services/superAdminAuthService';

const SuperAdminAuthContext = createContext();

export const SuperAdminAuthProvider = ({ children }) => {
  const [superAdmin, setSuperAdmin] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperAdminAuthenticated, setIsSuperAdminAuthenticated] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const currentSuperAdmin = superAdminAuthService.getUser();
        const hasToken = superAdminAuthService.isAuthenticated();

        if (currentSuperAdmin && hasToken) {
          const superAdminWithPermissions = {
            ...currentSuperAdmin,
            permissions: currentSuperAdmin.permissions || {}
          };
          setSuperAdmin(superAdminWithPermissions);
          setIsSuperAdminAuthenticated(true);
          superAdminAuthService.startAutoRefresh();
        } else {
          localStorage.removeItem('super_admin_token');
          localStorage.removeItem('super_admin_refresh_token');
          localStorage.removeItem('super_admin_data');
          setSuperAdmin(null);
          setIsSuperAdminAuthenticated(false);
        }
      } catch (error) {
        console.error('Super admin auth initialization error:', error);
        localStorage.removeItem('super_admin_token');
        localStorage.removeItem('super_admin_refresh_token');
        localStorage.removeItem('super_admin_data');
        setSuperAdmin(null);
        setIsSuperAdminAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const superAdminLogin = async (credentials) => {
    try {
      setIsLoading(true);
      const { user: superAdminData, token } = await superAdminAuthService.login(credentials);
      
      const superAdminWithPermissions = {
        ...superAdminData,
        permissions: superAdminData.permissions || {}
      };
      
      setSuperAdmin(superAdminWithPermissions);
      setIsSuperAdminAuthenticated(true);
      
      return { user: superAdminWithPermissions, token };
    } catch (error) {
      console.error('Super admin login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const superAdminLogout = async () => {
    try {
      superAdminAuthService.logout();
      setSuperAdmin(null);
      setIsSuperAdminAuthenticated(false);
    } catch (error) {
      console.error('Super admin logout error:', error);
    }
  };

  const value = {
    superAdmin,
    isLoading,
    isSuperAdminAuthenticated,
    superAdminLogin,
    superAdminLogout
  };

  return (
    <SuperAdminAuthContext.Provider value={value}>
      {children}
    </SuperAdminAuthContext.Provider>
  );
};

export const useSuperAdminAuth = () => {
  const context = useContext(SuperAdminAuthContext);
  if (!context) {
    throw new Error('useSuperAdminAuth must be used within a SuperAdminAuthProvider');
  }
  return context;
};

